import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import "./AnalyticsLineChart.css";
import IOSSwitch from "../IOSSwitch";
// ✅ DEFAULT COLORS for dynamic assignment
const DEFAULT_COLORS = [
    { color: '#8C1D5B', compareColor: '#F9A8D4' }, // Revenue
    { color: '#5F8F6B', compareColor: '#6EE7B7' }, // Spend
    { color: '#7A5BB6', compareColor: '#A5B4FC' }, // ROAS
    { color: '#D19A3A', compareColor: '#E7C37A' }, // CPOC
    { color: '#5A95D6', compareColor: '#9FC3EA' }, // OCTR
    { color: '#6FAFB0', compareColor: '#A8D8D9' }, // Sessions
    { color: '#4B556B', compareColor: '#9CA3AF' }, // Orders
];
// ✅ Legacy METRIC_CONFIG for backward compatibility
const METRIC_CONFIG = {
    "Channels Revenue": {
        format: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        color: "#8C1D5B",
        compareColor: "#F9A8D4",
        axis: 0,
    },
    "Shopify Revenue": {
        format: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        color: "#8C1D5B",
        compareColor: "#F9A8D4",
        axis: 0,
    },
    Revenue: {
        format: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        color: "#8C1D5B",
        compareColor: "#F9A8D4",
        axis: 0,
    },
    Spend: {
        format: (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        color: "#5F8F6B",
        compareColor: "#6EE7B7",
        axis: 0,
    },
    ROAS: {
        format: (v) => (v != null && !isNaN(v) ? v.toFixed(2) : "0.00"),
        color: "#7A5BB6",
        compareColor: "#A5B4FC",
        axis: 1,
    },
    "Channels ROAS": {
        format: (v) => (v != null && !isNaN(v) ? v.toFixed(2) : "0.00"),
        color: "#7A5BB6",
        compareColor: "#A5B4FC",
        axis: 1,
    },
    "ROAS Shopify": {
        format: (v) => (v != null && !isNaN(v) ? v.toFixed(2) : "0.00"),
        color: "#7A5BB6",
        compareColor: "#A5B4FC",
        axis: 1,
    },
    CPC: {
        format: (v) => (v != null && !isNaN(v) ? `$${v.toFixed(2)}` : "$0.00"),
        color: "#f59e0b",
        compareColor: "#FCD34D",
        axis: 0,
    },
    Sessions: {
        format: (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 }),
        color: "#6FAFB0",
        compareColor: "#A8D8D9",
        axis: 1,
    },
    Clicks: {
        format: (v) => v.toLocaleString(),
        color: "#0ea5e9",
        compareColor: "#7DD3FC",
        axis: 1,
    },
    "Outbound Clicks": {
        format: (v) => v.toLocaleString(),
        color: "#14b8a6",
        compareColor: "#5EEAD4",
        axis: 1,
    },
    CPOC: {
        format: (v) => `$${v.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`,
        color: "#D19A3A",
        compareColor: "#E7C37A", // lighter shade for comparison
        axis: 0,
    },
    OCTR: {
        format: (v) => (v != null && !isNaN(v) ? `${v.toFixed(2)}%` : "0.00%"),
        color: "#5A95D6",
        compareColor: "#9FC3EA", // lighter shade
        axis: 1,
    },
    Orders: {
        format: (v) => v != null && !isNaN(v)
            ? v.toLocaleString(undefined, { maximumFractionDigits: 0 })
            : "0",
        color: "#4B556B",
        compareColor: "#9CA3AF", // lighter gray
        axis: 1,
    },
};
// ✅ Get formatter based on metric name
const getMetricFormatter = (metricName) => {
    // Check if exists in legacy config
    if (METRIC_CONFIG[metricName]) {
        return METRIC_CONFIG[metricName].format;
    }
    // Dynamic formatting based on metric name patterns
    const lowerName = metricName.toLowerCase();
    if (lowerName.includes('revenue') || lowerName.includes('sales') || lowerName.includes('spend') || lowerName.includes('cost') || lowerName.includes('aov') || lowerName.includes('cpc') || lowerName.includes('cpoc')) {
        return (v) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (lowerName.includes('session') || lowerName.includes('click') || lowerName.includes('order') || lowerName.includes('impression')) {
        return (v) => (v != null && !isNaN(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0");
    }
    if (lowerName.includes('roas')) {
        return (v) => (v != null && !isNaN(v) ? v.toFixed(2) : "0.00");
    }
    if (lowerName.includes('rate') || lowerName.includes('ctr') || lowerName.includes('cvr') || lowerName.includes('conv')) {
        return (v) => (v != null && !isNaN(v) ? `${v.toFixed(2)}%` : "0.00%");
    }
    // Default formatter
    return (v) => (Number.isFinite(v) ? v.toLocaleString() : `${v}`);
};
// ✅ Determine Y-axis based on metric name
const getMetricAxis = (metricName) => {
    if (METRIC_CONFIG[metricName]?.axis !== undefined) {
        return METRIC_CONFIG[metricName].axis;
    }
    const lowerName = metricName.toLowerCase();
    // Right axis (1) for rates, ratios, counts
    if (lowerName.includes('roas') ||
        lowerName.includes('rate') ||
        lowerName.includes('ctr') ||
        lowerName.includes('cvr') ||
        lowerName.includes('sessions') ||
        lowerName.includes('clicks') ||
        lowerName.includes('orders') ||
        lowerName.includes('impressions')) {
        return 1;
    }
    // Left axis (0) for currency values
    return 0;
};
const formatFallback = (v) => Number.isFinite(v) ? v.toLocaleString() : `${v}`;
const AnalyticsLineChart = ({ categories = [], series = [], comparison, showLegend = true, height = 400, width = "100%", onCompareToggle, comapretoggle = 'bottom', compact = false, legendVariant = 'dots', }) => {
    const [showCompare, setShowCompare] = useState(false);
    const [hiddenMetrics, setHiddenMetrics] = useState(new Set());
    // ✅ FIXED: Check for comparison data properly
    const hasComparison = useMemo(() => {
        if (!comparison?.labels?.length)
            return false;
        if (!comparison?.series)
            return false;
        const seriesValues = Object.values(comparison.series);
        return seriesValues.some((v) => Array.isArray(v) && v.length > 0);
    }, [comparison]);
    const isCompareActive = showCompare && hasComparison;
    const dataType = useMemo(() => {
        if (!categories.length)
            return "daily";
        const first = categories[0];
        if (first.includes(":") || /^(\d{1,2})$/.test(first))
            return "hourly";
        return "daily";
    }, [categories]);
    const currentDataLength = useMemo(() => {
        return series.reduce((max, s) => Math.max(max, s.data?.length || 0), 0);
    }, [series]);
    const currentDisplayLength = useMemo(() => {
        return currentDataLength > 0 ? currentDataLength : categories.length;
    }, [currentDataLength, categories.length]);
    const compareDisplayLength = useMemo(() => {
        const compareLabelsLen = comparison?.labels?.length || 0;
        const compareSeriesLen = comparison?.series
            ? Math.max(0, ...Object.values(comparison.series).map((arr) => arr ? arr.length : 0))
            : 0;
        return Math.max(currentDisplayLength, categories.length, compareLabelsLen, compareSeriesLen);
    }, [comparison?.labels?.length, comparison?.series, currentDisplayLength, categories.length]);
    const displayLength = useMemo(() => {
        return isCompareActive ? compareDisplayLength : currentDisplayLength;
    }, [isCompareActive, compareDisplayLength, currentDisplayLength]);
    const hourlyHasDate = useMemo(() => {
        if (dataType !== "hourly")
            return false;
        const sample = categories[0] || comparison?.labels?.[0] || "";
        return /(\d{4}-\d{2}-\d{2})|(\d{1,2}\s+[A-Za-z]{3})|([A-Za-z]{3}\s+\d{1,2})/.test(sample);
    }, [dataType, categories, comparison?.labels]);
    const parseToDate = (str) => {
        if (!str)
            return null;
        const fullMatch = str.match(/(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})/);
        if (fullMatch) {
            const [, datePart, h, m] = fullMatch;
            const d = new Date(`${datePart}T${h.padStart(2, "0")}:${m.padStart(2, "0")}:00`);
            if (!isNaN(d.getTime()))
                return d;
        }
        const isoDate = str.match(/^(\d{4}-\d{2}-\d{2})$/);
        if (isoDate) {
            const d = new Date(`${isoDate[1]}T00:00:00`);
            if (!isNaN(d.getTime()))
                return d;
        }
        // "2024-01" (YYYY-MM format for months)
        const monthOnly = str.match(/^(\d{4})-(\d{2})$/);
        if (monthOnly) {
            const d = new Date(`${monthOnly[1]}-${monthOnly[2]}-01T00:00:00`);
            if (!isNaN(d.getTime()))
                return d;
        }
        const dayMonthMatch = str.match(/^(\d{1,2})\s+([A-Za-z]{3,})(?:\s+(\d{4}))?$/);
        if (dayMonthMatch) {
            const [, day, monthStr, year] = dayMonthMatch;
            const yearNum = year ? parseInt(year, 10) : new Date().getFullYear();
            const d = new Date(`${monthStr} ${day}, ${yearNum}`);
            if (!isNaN(d.getTime()))
                return d;
        }
        const monthDayMatch = str.match(/^([A-Za-z]{3,})\s+(\d{1,2})(?:,?\s*(\d{4}))?$/);
        if (monthDayMatch) {
            const [, monthStr, day, year] = monthDayMatch;
            const yearNum = year ? parseInt(year, 10) : new Date().getFullYear();
            const d = new Date(`${monthStr} ${day}, ${yearNum}`);
            if (!isNaN(d.getTime()))
                return d;
        }
        const timeOnly = str.match(/^(\d{1,2}):(\d{2})$/);
        if (timeOnly) {
            const now = new Date();
            now.setHours(parseInt(timeOnly[1], 10), parseInt(timeOnly[2], 10), 0, 0);
            return now;
        }
        const hourOnly = str.match(/^(\d{1,2})$/);
        if (hourOnly) {
            const h = parseInt(hourOnly[1], 10);
            if (h >= 0 && h <= 24) {
                const now = new Date();
                now.setHours(h, 0, 0, 0);
                return now;
            }
        }
        const generic = new Date(str);
        return isNaN(generic.getTime()) ? null : generic;
    };
    const formatAxisLabel = (raw, type, includeDateInHourly) => {
        const d = parseToDate(raw);
        if (!d)
            return raw;
        const day = d.getDate();
        const month = d.toLocaleString("en-US", { month: "short" });
        const hours = d.getHours().toString().padStart(2, "0");
        const mins = d.getMinutes().toString().padStart(2, "0");
        if (type === "daily")
            return `${day} ${month}`;
        // ✅ hourly with date: split into 2 lines
        if (includeDateInHourly)
            return `${day} ${month}\n${hours}:${mins}`;
        return `${hours}:${mins}`;
    };
    const extendLabels = (rawLabels, totalLength, type, includeDateInHourly) => {
        if (totalLength <= 0)
            return [];
        if (!rawLabels.length)
            return Array(totalLength).fill("");
        const result = rawLabels.map((l) => formatAxisLabel(l, type, includeDateInHourly));
        if (result.length >= totalLength)
            return result.slice(0, totalLength);
        let lastDate = parseToDate(rawLabels[rawLabels.length - 1]);
        if (!lastDate) {
            while (result.length < totalLength)
                result.push("");
            return result;
        }
        while (result.length < totalLength) {
            lastDate = new Date(lastDate.getTime() + (type === "hourly" ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000));
            const day = lastDate.getDate();
            const month = lastDate.toLocaleString("en-US", { month: "short" });
            const hours = lastDate.getHours().toString().padStart(2, "0");
            const mins = lastDate.getMinutes().toString().padStart(2, "0");
            if (type === "daily")
                result.push(`${day} ${month}`);
            else
                result.push(includeDateInHourly ? `${day} ${month} ${hours}:${mins}` : `${hours}:${mins}`);
        }
        return result;
    };
    const xLabels = useMemo(() => extendLabels(categories, displayLength, dataType, hourlyHasDate), [categories, displayLength, dataType, hourlyHasDate]);
    const hourlyLabelStep = useMemo(() => {
        if (dataType !== "hourly")
            return 1;
        // roughly 8 labels max
        const targetTicks = 8;
        return Math.max(1, Math.ceil(xLabels.length / targetTicks));
    }, [dataType, xLabels.length]);
    // ✅ FIXED: Build series with dynamic comparison support
    const { apexSeries, seriesMetaMap } = useMemo(() => {
        // Shortfall beyond the real data is padded with null (renders as a gap),
        // not 0 — a 0 here would draw a misleading flat "$0.00" line for hours
        // that simply have no data yet, indistinguishable from a real zero value.
        const pad = (arr) => {
            const base = arr ? arr.slice(0, displayLength) : [];
            const need = Math.max(0, displayLength - base.length);
            return [...base, ...Array(need).fill(null)];
        };
        const metaMap = [];
        // Build base series with colors
        const baseSeries = series.map((s, index) => {
            // Priority: 1) series.color, 2) METRIC_CONFIG color, 3) DEFAULT_COLORS
            const color = s.color || METRIC_CONFIG[s.name]?.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length].color;
            const compareColor = s.compareColor || METRIC_CONFIG[s.name]?.compareColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length].compareColor;
            metaMap.push({ name: s.name, isComparison: false, color });
            let axisIndex = s.yAxisIndex ?? getMetricAxis(s.name);
            if (axisIndex !== 1)
                axisIndex = 0;
            return {
                name: s.name,
                data: pad(s.data),
                color,
                compareColor, // Store for later use
                yAxisIndex: axisIndex,
            };
        });
        if (!isCompareActive) {
            return { apexSeries: baseSeries, seriesMetaMap: metaMap };
        }
        // ✅ FIXED: Build comparison series by matching series names
        const compareSeries = [];
        baseSeries.forEach((baseSer, index) => {
            const metricName = baseSer.name;
            // ✅ Try to find comparison data by series name (exact match)
            let compData = comparison?.series?.[metricName];
            // ✅ If not found, try lowercase match
            if (!compData) {
                const lowerName = metricName.toLowerCase();
                const compKeys = Object.keys(comparison?.series || {});
                const matchingKey = compKeys.find(k => k.toLowerCase() === lowerName);
                if (matchingKey) {
                    compData = comparison?.series?.[matchingKey];
                }
            }
            // ✅ If still not found, try by index (fallback)
            if (!compData) {
                const compKeys = Object.keys(comparison?.series || {});
                if (compKeys[index]) {
                    compData = comparison?.series?.[compKeys[index]];
                }
            }
            if (compData && Array.isArray(compData) && compData.length > 0) {
                const compareColor = baseSer.compareColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length].compareColor;
                metaMap.push({
                    name: metricName,
                    isComparison: true,
                    color: compareColor,
                });
                compareSeries.push({
                    name: "", // Empty name for comparison (shown as dotted)
                    data: pad(compData),
                    color: compareColor,
                    yAxisIndex: baseSer.yAxisIndex,
                });
            }
        });
        return {
            apexSeries: [...baseSeries, ...compareSeries],
            seriesMetaMap: metaMap,
        };
    }, [series, comparison, displayLength, isCompareActive]);
    const { visibleSeries, visibleMetaMap } = useMemo(() => {
        const vs = [];
        const vm = [];
        apexSeries.forEach((s, i) => {
            const meta = seriesMetaMap[i];
            if (meta && !hiddenMetrics.has(meta.name)) {
                vs.push(s);
                vm.push(meta);
            }
        });
        return { visibleSeries: vs, visibleMetaMap: vm };
    }, [apexSeries, seriesMetaMap, hiddenMetrics]);
    const finalChartSeries = visibleSeries;
    const getAxisMax = (seriesArr, axisIndex) => {
        const axisSeries = seriesArr.filter((s) => (s.yAxisIndex || 0) === axisIndex);
        if (!axisSeries.length)
            return undefined;
        const numeric = axisSeries
            .flatMap((s) => s.data || [])
            .map((v) => (v == null ? NaN : Number(v)))
            .filter((v) => !isNaN(v) && v > 0);
        if (!numeric.length)
            return undefined;
        return Math.ceil(Math.max(...numeric) * 1.1);
    };
    const leftMax = getAxisMax(finalChartSeries, 0);
    const rightMax = getAxisMax(finalChartSeries, 1);
    const dynamicYAxis = useMemo(() => {
        if (!finalChartSeries.length) {
            return [{ show: false, min: 0 }];
        }
        let leftShown = false;
        let rightShown = false;
        const axes = finalChartSeries.map((s) => {
            const isRight = s.yAxisIndex === 1;
            if (isRight) {
                const showThis = !rightShown;
                rightShown = true;
                return {
                    show: showThis,
                    opposite: true,
                    min: 0,
                    max: rightMax,
                    forceNiceScale: true,
                    tickAmount: 4,
                    labels: {
                        formatter: (val) => {
                            if (val == null || isNaN(val))
                                return "";
                            return val.toFixed(0);
                        },
                    },
                };
            }
            else {
                const showThis = !leftShown;
                leftShown = true;
                return {
                    show: showThis,
                    opposite: false,
                    min: 0,
                    max: leftMax,
                    forceNiceScale: true,
                    tickAmount: 4,
                    labels: {
                        formatter: (value) => {
                            if (value == null || isNaN(value))
                                return "";
                            if (value === 0)
                                return "$0";
                            if (value >= 1000000)
                                return `$${(value / 1000000).toFixed(1)}M`;
                            if (value >= 1000)
                                return `$${(value / 1000).toFixed(0)}K`;
                            return `$${value.toFixed(0)}`;
                        },
                    },
                };
            }
        });
        return axes;
    }, [finalChartSeries, leftMax, rightMax]);
    const hasLeftAxis = useMemo(() => finalChartSeries.some((s) => (s.yAxisIndex ?? 0) === 0), [finalChartSeries]);
    const leftPadding = hasLeftAxis ? 15 : 70;
    const formatTooltipDateTime = (raw, type) => {
        if (!raw)
            return "";
        const d = parseToDate(raw);
        if (!d)
            return raw;
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yyyy = d.getFullYear();
        if (type === "hourly") {
            const hh = String(d.getHours()).padStart(2, "0");
            const min = String(d.getMinutes()).padStart(2, "0");
            return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
        }
        return `${mm}/${dd}/${yyyy}`;
    };
    const chartOptions = useMemo(() => ({
        chart: {
            type: "line",
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        grid: {
            padding: {
                top: 15,
                right: 15,
                left: leftPadding,
                bottom: 0,
            },
        },
        stroke: {
            curve: "smooth",
            width: 2.5,
            dashArray: visibleMetaMap.map((m) => (m.isComparison ? 6 : 0)), // ✅ Dotted for comparison
        },
        colors: visibleSeries.map((s) => s.color), // ✅ Apply colors
        markers: { size: xLabels.length === 1 ? 5 : 0, hover: { size: 7 } },
        xaxis: {
            type: "category",
            categories: xLabels,
            tickAmount: compact
                ? (dataType === "hourly" ? Math.min(4, xLabels.length) : Math.min(5, xLabels.length))
                : (dataType === "hourly" ? Math.min(8, xLabels.length) : Math.min(12, xLabels.length)),
            labels: {
                rotate: 0,
                hideOverlappingLabels: true,
                style: { fontSize: compact ? "9px" : "10px", cssClass: "chart-xaxis-label-capitalize" },
                formatter: (val, _ts, opts) => {
                    if (!val)
                        return "";
                    // In compact mode, strip the date part from multiline labels (show only time)
                    if (compact && val.includes("\n")) {
                        return val.split("\n")[1] ?? val;
                    }
                    const i = opts?.i ?? 0;
                    if (dataType === "hourly" && xLabels.length > 12) {
                        const isFirst = i === 0;
                        const isLast = i === xLabels.length - 1;
                        if (!isFirst && !isLast && i % hourlyLabelStep !== 0)
                            return "";
                    }
                    return val;
                },
            },
        },
        yaxis: dynamicYAxis,
        legend: { show: false },
        tooltip: {
            shared: true,
            intersect: false,
            custom: ({ series: seriesData, dataPointIndex }) => {
                const currentDate = categories[dataPointIndex];
                const previousDate = comparison?.labels?.[dataPointIndex];
                const currentMetrics = [];
                const previousMetrics = [];
                visibleMetaMap.forEach((meta, idx) => {
                    let v = seriesData[idx]?.[dataPointIndex];
                    if (v === null || v === undefined || isNaN(v))
                        v = 0;
                    // Find the original series to get its color
                    const originalSeries = series.find(s => s.name === meta.name);
                    const displayColor = meta.isComparison
                        ? (originalSeries?.color || meta.color) // Use main color for compare tooltip text
                        : meta.color;
                    if (meta.isComparison) {
                        previousMetrics.push({ name: meta.name, value: v, color: displayColor });
                    }
                    else {
                        currentMetrics.push({ name: meta.name, value: v, color: meta.color });
                    }
                });
                const hasCurrentMetrics = currentMetrics.length > 0;
                const hasPreviousMetrics = previousMetrics.length > 0 && isCompareActive;
                const showCurrentColumn = hasCurrentMetrics;
                const showPreviousColumn = hasPreviousMetrics;
                const visibleColumns = (showCurrentColumn ? 1 : 0) + (showPreviousColumn ? 1 : 0);
                if (visibleColumns === 0)
                    return "";
                const isValidDate = (d) => d !== undefined && d !== null && d !== "" && d !== "Invalid date";
                const minWidth = isValidDate(currentDate) && isValidDate(previousDate) && isCompareActive
                    ? "420px"
                    : "200px";
                let html = `<div id="analytics-tooltip-panel" style="display:flex;gap:0px;padding:0px;border-radius:10px;background:rgba(11,15,25,0.85);box-shadow:0 8px 24px rgba(0,0,0,0.32);min-width:${minWidth};font-family:Inter,system-ui,sans-serif;">`;
                if (showCurrentColumn && currentDate) {
                    html += `<div style="flex:1; padding-bottom:4px;">
              <div id="analytics-tooltip-header-current" style="font-weight:700;font-size:12px;margin-bottom:0px;color:#fff;">
                ${formatTooltipDateTime(currentDate, dataType)}
              </div>`;
                    currentMetrics.forEach(({ name, value, color }) => {
                        const formatter = getMetricFormatter(name);
                        const formatted = formatter(value);
                        html += `<div style="display:flex;align-items:center;gap:6px;margin:0px 0;">
                <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
                <span style="color:#fff;font-size:13px;">${name}: <b>${formatted}</b></span>
              </div>`;
                    });
                    html += `</div>`;
                }
                if (showPreviousColumn && previousDate) {
                    const border = showCurrentColumn ? "border-left:1px solid rgba(255,255,255,0.15);padding-left:0px;padding-bottom:4px;" : "";
                    html += `<div style="flex:1;${border}; padding-bottom:4px;">
              <div id="analytics-tooltip-header-prev" style="font-weight:700;font-size:12px;margin-bottom:2px;color:#d1d5db;">
                ${formatTooltipDateTime(previousDate, dataType)}
              </div>`;
                    previousMetrics.forEach(({ name, value, color }) => {
                        const formatter = getMetricFormatter(name);
                        const formatted = formatter(value);
                        html += `<div style="display:flex;align-items:center;gap:6px;margin:0px 0;">
                <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
                <span style="color:#fff;font-size:13px;">${name}: <b>${formatted}</b></span>
              </div>`;
                    });
                    html += `</div>`;
                }
                html += `</div>`;
                return html;
            },
        },
    }), [visibleMetaMap, visibleSeries, xLabels, dataType, isCompareActive, categories, comparison, dynamicYAxis, series, leftPadding]);
    return (_jsxs("div", { className: "analytics-line-chart-wrapper", style: { position: "relative" }, children: [_jsx(Chart, { options: chartOptions, series: visibleSeries, type: "line", height: height, width: width }), legendVariant === "pills" ? (
            /* ── Pills variant: legend + toggle in one row ── */
            _jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 16px",
                    flexWrap: "wrap",
                    gap: "8px",
                    backgroundColor: "#F9FAFB",
                    borderTop: "1px solid #E5E7EB",
                    borderRadius: "0 0 8px 8px",
                }, children: [showLegend && (_jsx("div", { style: { display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }, children: seriesMetaMap
                            .filter((m) => !m.isComparison)
                            .filter((m, i, arr) => arr.findIndex((x) => x.name === m.name) === i)
                            .map((m) => {
                            const isHidden = hiddenMetrics.has(m.name);
                            const markerColor = isHidden ? "#D1D5DB" : m.color;
                            return (_jsxs("div", { onClick: () => setHiddenMetrics((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(m.name))
                                        next.delete(m.name);
                                    else
                                        next.add(m.name);
                                    return next;
                                }), style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    cursor: "pointer",
                                    opacity: isHidden ? 0.45 : 1,
                                    userSelect: "none",
                                    transition: "opacity 0.15s",
                                }, children: [_jsxs("span", { style: { position: "relative", display: "inline-block", width: "24px", height: "14px", flexShrink: 0 }, children: [_jsx("span", { style: { position: "absolute", top: "50%", left: 0, right: 0, height: "2px", backgroundColor: markerColor, borderRadius: "1px", transform: "translateY(-50%)" } }), _jsx("span", { style: {
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    width: "10px",
                                                    height: "10px",
                                                    borderRadius: "50%",
                                                    backgroundColor: markerColor,
                                                    border: "2px solid #ffffff",
                                                    boxSizing: "border-box",
                                                    zIndex: 1,
                                                } })] }), _jsx("span", { style: { fontSize: "12px", fontWeight: 400, color: isHidden ? "#9CA3AF" : "#374151" }, children: m.name })] }, m.name));
                        }) })), hasComparison && (_jsxs("div", { style: { display: "inline-flex", alignItems: "center", gap: "8px", flexShrink: 0 }, children: [_jsx(IOSSwitch, { checked: showCompare, onChange: (e) => {
                                    setShowCompare(e.target.checked);
                                    onCompareToggle?.(e.target.checked);
                                } }), _jsx("span", { style: { fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap" }, children: "Compare with prior period" })] }))] })) : (
            /* ── Default dots variant: original layout unchanged ── */
            _jsxs(_Fragment, { children: [_jsx("div", { style: { padding: "25px 0" }, children: showLegend && (_jsx("div", { style: {
                                display: "flex",
                                gap: "16px",
                                padding: "8px 0 4px 10px",
                                flexWrap: "wrap",
                                width: window.innerWidth < 768 ? "72%" : "",
                                ...(comapretoggle === "top" && { justifyContent: "center" }),
                            }, children: seriesMetaMap
                                .filter((m) => !m.isComparison)
                                .filter((m, i, arr) => arr.findIndex((x) => x.name === m.name) === i)
                                .map((m) => {
                                const isHidden = hiddenMetrics.has(m.name);
                                return (_jsxs("div", { onClick: () => setHiddenMetrics((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(m.name))
                                            next.delete(m.name);
                                        else
                                            next.add(m.name);
                                        return next;
                                    }), style: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        cursor: "pointer",
                                        opacity: isHidden ? 0.4 : 1,
                                        fontSize: "14px",
                                        color: "#374151",
                                        userSelect: "none",
                                    }, children: [_jsxs("span", { style: { display: "inline-flex", alignItems: "center" }, children: [_jsx("span", { style: { width: "9px", height: "2px", backgroundColor: isHidden ? "#d1d5db" : m.color, display: "inline-block" } }), _jsx("span", { style: { width: "8px", height: "8px", margin: "0 1px", borderRadius: "50%", backgroundColor: isHidden ? "#d1d5db" : m.color, display: "inline-block", flexShrink: 0 } }), _jsx("span", { style: { width: "9px", height: "2px", backgroundColor: isHidden ? "#d1d5db" : m.color, display: "inline-block" } })] }), _jsx("span", { children: m.name })] }, m.name));
                            }) })) }), _jsx("div", { className: "chart-footer-row", children: hasComparison && (_jsx("div", { className: "chart-compare-toggle all-Font-Sizes", style: comapretoggle === "top"
                                ? { top: "-45px", right: "20px" }
                                : comapretoggle === "bottom"
                                    ? { bottom: "10px", right: window.innerWidth < 768 ? "5px" : "10px" }
                                    : {}, children: _jsxs("label", { style: { fontSize: "13px" }, children: [_jsx("input", { type: "checkbox", checked: showCompare, onChange: (e) => {
                                            setShowCompare(e.target.checked);
                                            onCompareToggle?.(e.target.checked);
                                        } }), "Compare with prior period"] }) })) })] }))] }));
};
export default AnalyticsLineChart;
